import { useMemo, useState } from 'react';
import classNames from 'classnames';
import { ChevronDown } from 'lucide-react';
import { orDash } from '../../../../utils/schema-parse';
import { parseKmlString } from '../../../../utils/kml';
import type {
  RelatorioVisitaGrupo,
  RelatorioVisitaPonto,
  RelatorioVisitaResumo,
  RelatorioVisitaResumoOcorrencias,
  RelatorioVisitaTalhao,
} from '../../schemas/payload.schema';
import type {
  ResumoCard,
  ResumoMapa,
  ResumoTipoView,
} from './resumo-view.types';
import GlebaMapa from './gleba-mapa';
import PontoCard from './ponto-card';
import NdviModal from './ndvi-modal';
import MapaTalhaoModal from './mapa-talhao-modal';
import ResumoSection from './resumo-section';

interface GrupoSectionProps {
  gleba: RelatorioVisitaTalhao['gleba'];
  grupo: RelatorioVisitaGrupo;
  resumoOcorrencias?: RelatorioVisitaResumoOcorrencias;
}

const TITULO_POR_TIPO: Record<string, string> = {
  Ocorrência: 'Resumo de Ocorrências',
  'Aferição de Semeadura': 'Resumo da Aferição de Semeadura',
  'Aferição de Colheita': 'Resumo da Aferição de Colheita',
};

const chaveTitulo = (texto: string) =>
  texto
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[_\s]+/g, ' ')
    .trim()
    .toUpperCase();

const mapasUnicos = (itens: ResumoMapa[]) =>
  [
    ...new Map(
      itens
        .filter((item) => item.mapaInfestacao && item.mapaInfestacao !== '-')
        .map((item) => [item.mapaInfestacao, item]),
    ).values(),
  ].sort((a, b) => a.titulo.localeCompare(b.titulo, 'pt-BR'));

const cellValue = (value: unknown) =>
  orDash(Array.isArray(value) ? value.join(', ') : (value as string | number));

const cardsDoResumo = (
  resumo: NonNullable<RelatorioVisitaResumoOcorrencias>,
): ResumoCard[] =>
  (resumo.cards ?? []).map((card, indice) => ({
    chave: `${card.ordem ?? indice}-${orDash(card.titulo)}`,
    ordem: card.ordem ?? indice,
    titulo: orDash(card.titulo),
    mapaInfestacao: card.mapaInfestacao ?? null,
    indicadores: card.linhas ?? [],
  }));

const resumoDeOcorrencias = (
  resumo: RelatorioVisitaResumoOcorrencias | undefined,
): ResumoTipoView | null => {
  const cards = resumo ? cardsDoResumo(resumo) : [];

  if (cards.length === 0) return null;

  const titulo = orDash(resumo?.titulo).replace(/_/g, ' ');

  return {
    titulo,
    layout: 'grid_cards',
    tipo: titulo,
    iconeUrl: resumo?.icone ?? null,
    qtdPontos: resumo?.qtdPontos ?? null,
    showQtdPontos: true,
    tabelas: [],
    cards,
    mapasInfestacao: mapasUnicos(cards),
  };
};

const agruparResumosPorTipo = (
  pontos: RelatorioVisitaPonto[],
): ResumoTipoView[] => {
  const grupos = new Map<
    string,
    { tipo: string; qtdPontos: number; tabelas: RelatorioVisitaResumo[] }
  >();

  pontos.forEach((ponto) => {
    const tabelas = ponto.resumo ?? [];

    if (tabelas.length === 0) return;

    const tipo = ponto.tipo ?? '-';
    const grupo = grupos.get(tipo) ?? { tipo, qtdPontos: 0, tabelas: [] };

    grupo.qtdPontos += 1;
    grupo.tabelas.push(...tabelas);
    grupos.set(tipo, grupo);
  });

  return [...grupos.values()].map((grupo) => ({
    tipo: grupo.tipo,
    titulo: TITULO_POR_TIPO[grupo.tipo] ?? `Resumo de ${grupo.tipo}`,
    layout: 'lista' as const,
    iconeUrl: grupo.tabelas.find((tabela) => tabela.icone)?.icone ?? null,
    qtdPontos: grupo.qtdPontos || null,
    showQtdPontos: false,
    tabelas: grupo.tabelas,
    cards: [],
    mapasInfestacao: mapasUnicos(
      grupo.tabelas.map((tabela) => ({
        titulo: orDash(tabela.titulo),
        mapaInfestacao: tabela.mapaInfestacao ?? null,
      })),
    ),
  }));
};

const GrupoSection = ({
  gleba,
  grupo,
  resumoOcorrencias,
}: GrupoSectionProps) => {
  const { cabecalho, pontos = [] } = grupo;
  const ndvi = cabecalho?.ndvi;
  const linhas = cabecalho?.linhas ?? [];
  const headers = linhas[0]?.campos ?? [];

  const [open, setOpen] = useState(true);
  const [ndviOpen, setNdviOpen] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);

  const glebaParsed = useMemo(() => parseKmlString(gleba?.kml), [gleba?.kml]);

  const hasMap =
    glebaParsed.polygons.length > 0 ||
    pontos.some((ponto) => ponto.latitude != null && ponto.longitude != null);

  const resumosPorTipo = useMemo(() => {
    const ocorrencias = resumoDeOcorrencias(resumoOcorrencias);

    if (!ocorrencias) return agruparResumosPorTipo(pontos);

    const chave = chaveTitulo(ocorrencias.titulo);
    const dosPontos = agruparResumosPorTipo(pontos).filter(
      (resumo) => chaveTitulo(resumo.titulo) !== chave,
    );

    return [...dosPontos, ocorrencias];
  }, [resumoOcorrencias, pontos]);

  const areaLabel =
    cabecalho?.areaHa != null ? ` (${cabecalho.areaHa} ha)` : '';

  return (
    <div className="rv-grupo">
      <div className="rv-grupo-card">
        <div className="rv-grupo-head">
          <button
            type="button"
            aria-label={open ? 'Recolher' : 'Expandir'}
            className="rv-grupo-toggle"
            onClick={() => setOpen((previous) => !previous)}
          >
            <ChevronDown
              className="rv-grupo-chevron"
              size={20}
              style={{ transform: open ? 'rotate(0deg)' : 'rotate(-180deg)' }}
            />
          </button>

          <button
            className="rv-grupo-title"
            type="button"
            onClick={() => setOpen((previous) => !previous)}
          >
            {`${orDash(gleba?.descricao)}${
              cabecalho?.descricao && cabecalho.descricao !== '-'
                ? ` - ${cabecalho.descricao}`
                : ''
            }${areaLabel}`}
          </button>

          <div className="rv-grupo-thumbs">
            {ndvi?.imagem ? (
              <button
                type="button"
                title="NDVI"
                className="rv-grupo-thumb"
                onClick={() => setNdviOpen(true)}
              >
                <img
                  alt="NDVI"
                  className="rv-grupo-thumb-img"
                  src={ndvi.imagem}
                />
              </button>
            ) : null}
            {hasMap ? (
              <button
                type="button"
                title="Mapa do talhão"
                className="rv-grupo-thumb"
                onClick={() => setMapOpen(true)}
              >
                <div className="rv-grupo-thumb-preview">
                  <GlebaMapa parsed={glebaParsed} pontos={pontos} altura={40} />
                </div>
              </button>
            ) : null}
          </div>
        </div>

        {headers.length > 0 ? (
          <div className="rv-grupo-body">
            {headers.length > 0 ? (
              <div className="rv-linhas-table">
                <table
                  className="rv-grupo-table"
                  style={{ tableLayout: 'fixed' }}
                >
                  <colgroup>
                    {headers.map((campo) => (
                      <col
                        key={campo.label}
                        style={{ width: `${100 / headers.length}%` }}
                      />
                    ))}
                  </colgroup>
                  <thead>
                    <tr>
                      {headers.map((campo) => (
                        <th key={campo.label} className="rv-grupo-th">
                          {orDash(campo.label)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {linhas.map((linha) => (
                      <tr key={linha.ordem} className="rv-grupo-row">
                        {(linha.campos ?? []).map((campo) => (
                          <td
                            key={campo.label}
                            className={classNames('rv-grupo-cell', {
                              'rv-grupo-cell--destaque': !!campo.cor,
                            })}
                            style={campo.cor ? { color: campo.cor } : undefined}
                          >
                            {cellValue(campo.value)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}

            <div className="rv-linhas-mobile">
              {linhas.map((linha) => (
                <div key={linha.ordem} className="rv-grupo-m-row">
                  {(linha.campos ?? []).map((campo) => (
                    <div key={campo.label} className="rv-grupo-m-field">
                      <span className="rv-grupo-m-label">
                        {orDash(campo.label)}
                      </span>
                      <span
                        className={classNames('rv-grupo-m-value', {
                          'rv-grupo-m-value--destaque': !!campo.cor,
                        })}
                        style={campo.cor ? { color: campo.cor } : undefined}
                      >
                        {cellValue(campo.value)}
                      </span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {open && pontos.length > 0 ? (
          <div className="rv-grupo-pontos">
            {pontos.map((ponto) => (
              <PontoCard
                key={ponto.ordem}
                glebaParsed={glebaParsed}
                ponto={ponto}
                tituloMapa={`Mapa do Talhão ${orDash(gleba?.descricao)}`}
              />
            ))}
          </div>
        ) : null}

        {open && resumosPorTipo.length > 0 ? (
          <div className="rv-grupo-resumo">
            {resumosPorTipo.map((resumoTipo) => (
              <ResumoSection
                key={resumoTipo.tipo}
                resumo={resumoTipo}
                mapasInfestacao={resumoTipo.mapasInfestacao}
                tituloTalhao={orDash(gleba?.descricao)}
              />
            ))}
          </div>
        ) : null}
      </div>

      {ndvi ? (
        <NdviModal
          ndvi={ndvi}
          open={ndviOpen}
          onClose={() => setNdviOpen(false)}
        />
      ) : null}

      {hasMap ? (
        <MapaTalhaoModal
          open={mapOpen}
          parsed={glebaParsed}
          pontos={pontos}
          titulo={`Mapa do Talhão ${orDash(gleba?.descricao)}`}
          onClose={() => setMapOpen(false)}
        />
      ) : null}
    </div>
  );
};

export default GrupoSection;
